/**
 * PIN FLIGHT — shared contract.
 *
 * Written FIRST, before the feature, so the pieces (prefetch trigger, tow
 * layer, flyer, map landing) can be built against a fixed interface instead
 * of guessing at each other.
 *
 * The gesture: buyer clicks Search on /marketplace → the location icon leaves
 * the "Near" field → it flies a randomised route across the towing pages →
 * it plants on the map as the search anchor for the place they typed.
 *
 * Design source of truth: design-explorations/pink-bead/{tow,dashed-icon}.html
 * (gitignored — backed up under the session scratchpad's RESTORE/mocks/).
 */

/**
 * The searched-location marker's colour — a muted plum that sits apart from the
 * map's green pins (pickup `primary.dark`, producer `primary.light`) so YOUR
 * location reads as a different kind of thing from the farms around it, without
 * clashing with the earthy palette.
 *
 * There's no purple in the MUI theme, so unlike the green pins these are literal
 * constants and both consumers (the flyer and the map anchor) import them here —
 * one source of truth, so the pin that flies and the pin it becomes can't drift
 * apart in colour.
 *
 * `_DEEP` is the fill, `_MAIN` the trail/ripple: a touch of depth top-to-bottom,
 * the same relationship `primary.dark`/`primary.main` had.
 */
export const FLIGHT_INK_MAIN = '#7A5C9E';
export const FLIGHT_INK_DEEP = '#5E4680';

/* ------------------------------------------------------------------ *
 * Timing
 * ------------------------------------------------------------------ */

/**
 * Beats, in ms from the Search click. Lifted from tow.html after the mock was
 * scrubbed frame by frame.
 *
 * TOW_END === TOUCHDOWN is load-bearing, not a coincidence. The pin commits to
 * a point on the map; if the pages were still settling after it planted, the
 * map would slide out from under a pin that had already landed. Keep them equal.
 */
export const FLIGHT_TIMING = {
  /** icon climbs out of the Near field's MyLocationIcon socket */
  EMERGE: 160,
  /** icon launches; the dashed route starts drawing with it */
  LAUNCH: 300,
  /**
   * the tow begins — deliberately LATE, so the emergence is seen against the
   *  home page instead of being wiped by the incoming one
   */
  TOW_START: 500,
  TOW_DURATION: 700,
  /** pages settled AND pin touchdown — these must coincide (see above) */
  TOUCHDOWN: 1200,
  /** the per-route landing beat */
  LANDING_DURATION: 620,
  /** map pins start landing after the flown pin has planted */
  PINS_START: 1400,
  PIN_STAGGER: 60,
} as const;

/** Total wall time of the whole gesture, for cleanup/unmount scheduling. */
export const FLIGHT_TOTAL_MS = 2100;

/* ------------------------------------------------------------------ *
 * Routes
 * ------------------------------------------------------------------ */

/**
 * The mocks explored a fourth route, 'turnByTurn' — right-angle segments like a
 * street route, with a crosshair lock instead of a plant. Cut: among curves that
 * all read as flight, the straight runs read as a different kind of object
 * entirely, and the odd one out is the one you notice on the tenth search.
 */
export type RouteId = 'arc' | 'loop' | 'slingshot';

export const ROUTE_IDS: readonly RouteId[] = ['arc', 'loop', 'slingshot'];

/**
 * Routes are authored against the mock's 980x610 plane, flying from the Near
 * field's icon (435,258) to the map's search anchor (756,336). Both endpoints
 * were MEASURED off the rendered mock, not eyeballed.
 *
 * At runtime the authored curve is affine-mapped onto the real on-screen
 * start/end (see buildRoutePath). Keeping the authored coordinates verbatim
 * means the shapes stay exactly as they were designed and reviewed.
 */
const AUTHORED_FROM = { x: 435, y: 258 };
const AUTHORED_TO = { x: 756, y: 336 };

/**
 * Authored path data, one entry per route. Single source of truth — this string
 *  feeds the flight (offset-path), the dashed line, and the reveal mask alike.
 *  Never re-type a path; drift between them is a whole bug class.
 */
const AUTHORED_ROUTES: Record<RouteId, string> = {
  arc: 'M435,258 C 524,150 672,148 756,336',
  loop:
    'M435,258 C 498,226 542,214 574,224 C 616,237 622,292 586,302 ' +
    'C 549,312 543,254 585,238 C 641,217 702,248 756,336',
  slingshot: 'M435,258 C 372,262 366,298 442,303 C 566,311 668,296 756,336',
};

export interface Point {
  x: number;
  y: number;
}

/**
 * Map an authored route onto a real start/end pair.
 *
 * Non-uniform scale (independent x and y factors) rather than a similarity
 * transform: it keeps the endpoints exact, which is what matters — the pin must
 * leave the field and hit the anchor. It does mean a very lopsided real delta
 * will stretch the loop, which is acceptable and bounded by clamping below.
 * @param id - Which authored route to map.
 * @param from - Real start point (viewport coords).
 * @param to - Real end point (viewport coords).
 * @returns The SVG path string mapped onto the real start/end pair.
 */
export function buildRoutePath(id: RouteId, from: Point, to: Point): string {
  const authoredDx = AUTHORED_TO.x - AUTHORED_FROM.x;
  const authoredDy = AUTHORED_TO.y - AUTHORED_FROM.y;
  const realDx = to.x - from.x;
  const realDy = to.y - from.y;

  // Guard the degenerate case: a near-zero delta would blow the scale up and
  // fling the control points off-screen. Below this the flight isn't worth
  // drawing anyway — callers should skip it.
  const sx = Math.abs(authoredDx) < 1 ? 1 : realDx / authoredDx;
  const sy = Math.abs(authoredDy) < 1 ? 1 : realDy / authoredDy;

  return AUTHORED_ROUTES[id].replace(
    /(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/g,
    (_m, xs: string, ys: string) => {
      const x = from.x + (Number(xs) - AUTHORED_FROM.x) * sx;
      const y = from.y + (Number(ys) - AUTHORED_FROM.y) * sy;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    },
  );
}

/**
 * Pick a route at random. Every search takes a different way across.
 * @returns A randomly chosen route id.
 */
export function pickRoute(): RouteId {
  return ROUTE_IDS[Math.floor(Math.random() * ROUTE_IDS.length)];
}

/* ------------------------------------------------------------------ *
 * The flight request — what the trigger hands to the flyer
 * ------------------------------------------------------------------ */

export interface FlightRequest {
  /**
   * Viewport coords of the Near field's icon, captured at click time —
   *  BEFORE navigation, because the search bar is towed off screen and its
   *  element is gone long before the pin arrives.
   */
  from: Point;
  route: RouteId;
  /**
   * Monotonic id so a second search restarts the flight cleanly rather than
   *  merging with the first.
   */
  id: number;
}

/* ------------------------------------------------------------------ *
 * The bus
 *
 * The trigger (search-selector, on the OUTGOING page) and the flyer (mounted
 * in the always-mounted App shell) are on opposite sides of a route change, so
 * they cannot share React state or context that survives it. A tiny module-level
 * emitter is the honest way across: no provider, no re-render, no dependency on
 * either tree staying mounted.
 * ------------------------------------------------------------------ */

type FlightListener = (req: FlightRequest) => void;

const listeners = new Set<FlightListener>();
let nextId = 1;

/** performance.now() at which the in-flight pin is due to plant, if any. */
let landingDueAt: number | null = null;

/**
 * Called by the Search click handler, just before navigate().
 * @param from - Viewport coords of the Near field's icon at click time.
 * @param route - Route to fly; a random one when not specified.
 */
export function requestPinFlight(
  from: Point,
  route: RouteId = pickRoute(),
): void {
  const req: FlightRequest = { from, route, id: nextId++ };
  landingDueAt = performance.now() + FLIGHT_TIMING.TOUCHDOWN;
  listeners.forEach((l) => l(req));
}

/**
 * Both readers below are deliberately CLOCK READS, called during render, and
 * return a countdown rather than a timestamp.
 *
 * The tempting alternative — capture the countdown once in an effect and hand it
 * to CSS as a delay — silently breaks: a CSS `animation-delay` starts when the
 * element renders, so any lag between capturing the number and painting it is
 * added on top. That drift produced a measured 280ms window where the flown pin
 * had already gone and the map's pin had not yet appeared: a blink with no pin
 * on screen at all.
 *
 * Reading the clock at render instead makes it self-correcting — a late render
 * simply gets a shorter delay and still fires on the same absolute beat.
 *
 * Neither function mutates anything: a stale flight just reads as 0 (= "no
 * hold"), and the next requestPinFlight overwrites landingDueAt anyway. They
 * must stay side-effect free precisely BECAUSE they're called during render.
 * @param target - Absolute performance.now() timestamp to count down to.
 * @returns Milliseconds until `target`, or 0 when past/stale/no flight.
 */
function countdown(target: number): number {
  if (landingDueAt === null) return 0;
  const remaining = target - performance.now();
  // Past, or implausibly far out (a stale flight we never cleaned up) → no hold,
  // which is exactly today's behaviour.
  if (remaining <= 0 || remaining > FLIGHT_TOTAL_MS) return 0;
  return remaining;
}

/**
 * How long the map should hold its pins before letting them land, so they
 * follow the flown pin instead of beating it.
 *
 * The map's own trigger (frameKey) fires when DATA arrives, which — now that we
 * prefetch — is very nearly instant. Left alone the producer pins would land at
 * ~0ms while the flown pin is still in the air until 1200ms, so the consequence
 * would arrive before its cause and the gesture would read backwards.
 *
 * The map has no idea when the Search click happened; only this bus does. It
 * asks, and gets 0 whenever there's no flight (an ordinary filter change, a
 * direct link to /marketplace/search, reduced motion), which leaves the normal
 * behaviour untouched.
 * @returns Milliseconds the map should hold its pins; 0 when no flight.
 */
export function msUntilPinsMayLand(): number {
  return landingDueAt === null ? 0 : countdown(landingDueAt);
}

/**
 * When the map's own search-anchor pin should appear: as the flown pin FINISHES
 * landing, not when it touches down — the landing beat plays on the flown pin,
 * and the anchor is what's left behind afterwards.
 *
 * Revealed slightly early so the two identical pins overlap for a frame or two
 * on the same pixel (invisible) instead of leaving a gap (a blink).
 */
export const ANCHOR_HANDOFF_OVERLAP_MS = 60;

export function msUntilAnchorReveal(): number {
  if (landingDueAt === null) return 0;
  return countdown(
    landingDueAt + FLIGHT_TIMING.LANDING_DURATION - ANCHOR_HANDOFF_OVERLAP_MS,
  );
}

export function onPinFlight(listener: FlightListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/* ------------------------------------------------------------------ *
 * Reduced motion
 * ------------------------------------------------------------------ */

/**
 * Read live rather than cached — the user can flip this mid-session, and the
 * whole gesture must degrade to "the page is simply there, the pin is simply
 * there". This is a requirement, not a nicety.
 * @returns True when the user currently prefers reduced motion.
 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}
